import { CardListType, CardType, PriceMapType } from '@/types/card'
import { sortByModes } from '@/utils/constants'

// Alters the mode if the current mode doesn't apply to the current cardviews
export const fixMode = (mode: string, cardLists: CardListType[]) => {
  const cardviews = cardLists.flatMap((cardList) => cardList.cardviews)
  const countModeAvailable = cardviews.some((cardview) => cardview.num_decks)
  if (
    (mode === 'count' && !countModeAvailable) ||
    (mode === 'percent' && !cardviews.some((cardview) => cardview.potential_decks)) ||
    (mode === 'synergy' && !cardviews.some((cardview) => cardview.synergy))
  ) {
    if (countModeAvailable) {
      mode = 'count'
    } else {
      mode = 'name'
    }
  }

  switch (mode) {
    case 'percent': {
      const modeAvailable = cardLists
        .flatMap((cardList) => cardList.cardviews)
        .some((cardview) => cardview.potential_decks)
      if (!modeAvailable) {
        mode = 'count'
      }
      break
    }
    case 'count': {
      const modeAvailable = cardLists.flatMap((cardList) => cardList.cardviews).some((cardview) => cardview.num_decks)
      if (!modeAvailable) {
        mode = 'name'
      }
      break
    }
    case 'synergy': {
      const modeAvailable = cardLists.flatMap((cardList) => cardList.cardviews).some((cardview) => cardview.synergy)
      if (!modeAvailable) {
        mode = 'count'
      }
      break
    }
  }

  return mode
}

type Props = {
  cardLists: CardListType[]
  cardMap: Record<string, CardType>
  priceSource: keyof PriceMapType
  sortedBy: (typeof sortByModes)[number]['key']
}

const sort = ({ cardLists, cardMap, priceSource, sortedBy }: Props) => {
  sortedBy = fixMode(sortedBy, cardLists)
  switch (sortedBy) {
    case 'default':
      return cardLists
    case 'percent':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort(
          (a, b) => b.num_decks / b.potential_decks - a.num_decks / a.potential_decks,
        ),
      }))
    case 'count':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => b.num_decks - a.num_decks),
      }))
    case 'synergy':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => b.synergy - a.synergy),
      }))
    case 'name':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => a.name.localeCompare(b.name)),
      }))
    case 'mana':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => cardMap[a.sanitized]?.cmc - cardMap[b.sanitized]?.cmc),
      }))
    case 'price':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort(
          (a, b) =>
            (cardMap[a.cards ? a.cards[0].url : a.sanitized]?.prices[priceSource]?.price || Infinity) -
            (cardMap[b.cards ? b.cards[0].url : b.sanitized]?.prices[priceSource]?.price || Infinity),
        ),
      }))
    case 'released_at':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort(
          (a, b) =>
            new Date(cardMap[b.sanitized]?.released_at).getTime() -
            new Date(cardMap[a.sanitized]?.released_at).getTime(),
        ),
      }))
    case 'salt':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => cardMap[b.sanitized]?.salt - cardMap[a.sanitized]?.salt),
      }))
    default:
      throw new Error(`Unknown sort mode: ${sortedBy}`)
  }
}

export default sort
